// SendLogPanel — the durable send history (Suresh 2026-10-01).
// Statements and certificates are DECOUPLED flows; this panel keeps them
// separate with a kind filter, and failures are first-class rows with
// their error text (previously they vanished with the alert popup).
import { useState, useEffect } from 'react';
import { fetchJson } from '../api.js';

const STATUS_STYLE = {
  'sent':                  { color: '#15803d', label: 'sent' },
  'dry-run':               { color: '#0369a1', label: 'dry-run' },
  'skipped_no_recipients': { color: '#c05100', label: 'no recipients' },
  'failed':                { color: '#b91c1c', label: 'FAILED' }
};

export default function SendLogPanel({ cycleKey }) {
  const [kind, setKind] = useState('');        // '' = both
  const [status, setStatus] = useState('');
  const [thisCycleOnly, setThisCycleOnly] = useState(true);
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);

  async function load() {
    setErr(null);
    try {
      const qs = new URLSearchParams({ limit: '300' });
      if (kind) qs.set('kind', kind);
      if (status) qs.set('status', status);
      if (thisCycleOnly && cycleKey) qs.set('cycle_key', cycleKey);
      const j = await fetchJson('/api/send-log?' + qs.toString());
      setRows(j.rows || []);
    } catch (e) { setErr(e.message); setRows([]); }
  }
  useEffect(() => { load(); }, [kind, status, thisCycleOnly, cycleKey]);

  const failedCount = (rows || []).filter(r => r.status === 'failed').length;

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
        <strong>📨 Send log</strong>
        <select className="refresh-btn small" value={kind} onChange={e => setKind(e.target.value)}>
          <option value="">Statements + Certificates</option>
          <option value="statement">Statements only</option>
          <option value="certificate">Certificates only</option>
        </select>
        <select className="refresh-btn small" value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="skipped_no_recipients">No recipients</option>
          <option value="dry-run">Dry-run</option>
        </select>
        <label className="small" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <input type="checkbox" checked={thisCycleOnly} onChange={e => setThisCycleOnly(e.target.checked)} />
          this cycle only ({cycleKey})
        </label>
        <button className="refresh-btn small" onClick={load}>Refresh</button>
        {failedCount > 0 && <span style={{ color: '#b91c1c', fontWeight: 700 }}>⚠ {failedCount} failure(s) shown</span>}
      </div>
      {err && <div className="error small">{err}</div>}
      {rows === null && <div className="small" style={{ color: '#888' }}>Loading…</div>}
      {rows && rows.length === 0 && !err && <div className="small" style={{ color: '#666' }}>No send activity matches the filters.</div>}
      {rows && rows.length > 0 && (
        <table className="fs-table tidy" style={{ fontSize: '0.88em' }}>
          <thead><tr>
            <th>When</th><th>Type</th><th>Customer</th><th>Cert #</th><th>Status</th><th>To / Cc</th><th>Error</th><th>By</th>
          </tr></thead>
          <tbody>
            {rows.map(r => {
              const st = STATUS_STYLE[r.status] || { color: '#333', label: r.status };
              const to = Array.isArray(r.recipients_to) ? r.recipients_to : [];
              const cc = Array.isArray(r.recipients_cc) ? r.recipients_cc : [];
              return (
                <tr key={r.id} style={r.status === 'failed' ? { background: '#fef2f2' } : undefined}>
                  <td className="small" style={{ whiteSpace: 'nowrap' }}>{r.at}</td>
                  <td>
                    <span style={{
                      fontSize: '0.8em', fontWeight: 700, padding: '0.1rem 0.4rem', borderRadius: 4,
                      background: r.kind === 'statement' ? '#ede9fe' : '#e0f2fe',
                      color: r.kind === 'statement' ? '#5b21b6' : '#075985'
                    }}>{r.kind === 'statement' ? 'STMT' : 'CERT'}</span>
                  </td>
                  <td style={{ fontWeight: 600 }}>{r.customer}</td>
                  <td className="small">{r.cert_number || '—'}</td>
                  <td style={{ color: st.color, fontWeight: 700 }}>{st.label}</td>
                  <td className="small" style={{ maxWidth: 280 }}>
                    {to.join(', ')}{cc.length > 0 ? ' · cc: ' + cc.join(', ') : ''}
                    {to.length + cc.length === 0 ? '—' : ''}
                  </td>
                  <td className="small" style={{ color: '#b91c1c', maxWidth: 260 }}>{r.error || ''}</td>
                  <td className="small">{r.sent_by || ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
